import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/**
 * EmailTheming keeps the resolved theme (our extendTheme overrides) in a hidden
 * `globalContent` node, but only seeds it once, when the view mounts. Anything
 * that replaces the whole document afterwards (setContent for a draft, a
 * reply, a template) drops that node, and both the editor and
 * getEmailHTML() silently fall back to the stock "basic" theme: white body
 * and container, padded paragraphs. Carry the previous theme data across so
 * the overrides survive.
 */
const GLOBAL = "globalContent";

function findGlobal(doc: PMNode): { pos: number; data: Record<string, unknown> } | null {
    const found: { pos: number; data: Record<string, unknown> }[] = [];
    doc.descendants((node, pos) => {
        if (found.length) return false;
        if (node.type.name === GLOBAL) found.push({ pos, data: (node.attrs.data ?? {}) as Record<string, unknown> });
        return !found.length;
    });
    return found[0] ?? null;
}

export const KeepEmailTheme = Extension.create({
    name: "keepEmailTheme",
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("keepEmailTheme"),
                appendTransaction(transactions, oldState, newState) {
                    if (!transactions.some(tr => tr.docChanged)) return null;
                    const type = newState.schema.nodes[GLOBAL];
                    if (!type) return null;
                    const prev = findGlobal(oldState.doc);
                    if (!prev?.data.styles) return null;
                    const next = findGlobal(newState.doc);
                    if (next?.data.styles) return null;
                    const data = { ...prev.data, ...next?.data, styles: prev.data.styles };
                    const tr = newState.tr;
                    if (next) tr.setNodeAttribute(next.pos, "data", data);
                    else tr.insert(0, type.create({ data }));
                    return tr.setMeta("addToHistory", false);
                },
            }),
        ];
    },
});
